import { DownOutlined, PrinterOutlined, TeamOutlined } from "@ant-design/icons";
import { Button, Checkbox, Divider, Modal, Table, Tag, Typography } from "antd";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useState, useRef, useCallback } from "react";
import { BsBoxSeam } from "react-icons/bs";
import { HiOutlineUsers } from "react-icons/hi2";
import { LuTruck } from "react-icons/lu";
import { MdOutlineLocationOn } from "react-icons/md";
import { TbWallet } from "react-icons/tb";

import mnjLogo from "@/assets/mnj-logo.jpg";
import { features } from "@/config/features";
import { useSubcontractorName } from "@/hooks/useGetSubcontractors";
import {
  IBulkStageSelection,
  IProjectCostInput,
  ITaskAllocation
} from "@/pages/ForecastingPage";
import { IForecastingCentralRepoRes } from "@/types/forecasting";
import { getLabourDayRate } from "@/utils/labour";
import { getMaterialPercent } from "@/utils/material";

type PrintSection =
  | "labourCosts"
  | "plantEquipment"
  | "materials"
  | "subcontractors"
  | "plotDetail"
  | "forecastVsBudget";

const PRINT_SECTIONS: { key: PrintSection; label: string }[] = [
  { key: "labourCosts", label: "Labour Costs" },
  { key: "plantEquipment", label: "Plant & Equipment" },
  { key: "materials", label: "Materials" },
  { key: "subcontractors", label: "Subcontractors" },
  { key: "plotDetail", label: "Plot Detail" },
  { key: "forecastVsBudget", label: "Forecast vs Tender Budget Summary" }
];

const { Title, Text } = Typography;

export const SummaryStep = ({
  projectCentralRepoData,
  projectCosts,
  variations,
  bulkStageSelections,
  selectedPlots,
  taskAllocations,
  summaryExpandedSections,
  toggleSummarySection,
  selectedMonth
}: {
  projectCentralRepoData: IForecastingCentralRepoRes;
  projectCosts: IProjectCostInput;
  variations: Array<{
    id: string;
    variation: string;
    utilizedAmount: number;
    availableAmount: number;
    totalAmount: number;
  }>;
  bulkStageSelections: IBulkStageSelection[];
  selectedPlots: Set<string>;
  taskAllocations: ITaskAllocation[];
  summaryExpandedSections: {
    projectCostDetails: boolean;
    plotDetails: boolean;
  };
  toggleSummarySection: (section: "projectCostDetails" | "plotDetails") => void;
  selectedMonth?: string;
}) => {
  const getSubcontractorName = useSubcontractorName();

  // Calculate totals
  const totalLabour = projectCosts.labourCosts.reduce(
    (sum, c) => sum + c.amount,
    0
  );
  const totalPlant = projectCosts.plantCosts.reduce(
    (sum, c) => sum + c.amount,
    0
  );
  const totalMaterial = projectCosts.materialCosts.reduce(
    (sum, c) => sum + c.amount,
    0
  );
  const totalSubcontractor = projectCosts.subcontractorCosts.reduce(
    (sum, c) => sum + c.amount,
    0
  );
  const variationCost =
    variations?.length > 0
      ? variations.reduce(
          (acc, cur) => acc + (cur.utilizedAmount > 0 ? cur.utilizedAmount : 0),
          0
        )
      : 0;
  const totalBudget =
    totalLabour +
    totalPlant +
    totalMaterial +
    totalSubcontractor +
    variationCost;

  // Build final assignments from bulkStageSelections
  const finalAssignments = [...selectedPlots].map((plotNumber) => {
    const stages = bulkStageSelections
      .filter((sel) => sel.plotNumbers.includes(plotNumber))
      .map((sel) => ({ id: sel.stageId, name: sel.stageId }));
    return { plotNumber, stages };
  });

  // Stage colors for display
  const stageColors: Record<string, string> = {
    "Cut/Fill": "#1890ff",
    "Foundation - Exc & Conc": "#52c41a",
    "Foundation - Walls to DPC": "#faad14",
    "Foundation Floor": "#eb2f96",
    "Private Drives to Binder": "#722ed1",
    "Private Drives to Surface Course": "#13c2c2",
    "Shared Drives to Surface Course": "#fa541c",
    "Plot Services": "#2f54eb",
    "Service Connections": "#a0d911",
    "Plot Paving": "#f5222d",
    "Scaffold Platform": "#1890ff",
    "Topsoil/Plot Clearance": "#52c41a",
    "Screen Walls": "#faad14"
  };

  // Print dialog state
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [selectedPrintSections, setSelectedPrintSections] = useState<
    Set<PrintSection>
  >(new Set(["labourCosts"]));
  const [isPrinting, setIsPrinting] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  const togglePrintSection = (section: PrintSection) => {
    setSelectedPrintSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  const handlePrint = useCallback(async () => {
    if (selectedPrintSections.size === 0) return;
    setIsPrinting(true);

    try {
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4"
      });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const contentWidth = pageWidth - margin * 2;

      // Determine which groups go on separate pages
      const costSections: PrintSection[] = [
        "labourCosts",
        "plantEquipment",
        "materials",
        "subcontractors"
      ];
      const hasAnyCostSection = costSections.some((s) =>
        selectedPrintSections.has(s)
      );
      const hasPlotDetail = selectedPrintSections.has("plotDetail");
      const hasForecastVsBudget = selectedPrintSections.has("forecastVsBudget");

      // Helper: render a DOM element to a canvas then add to PDF
      const addElementToPdf = async (
        element: HTMLElement,
        startNewPage: boolean,
        yOffset: number = margin
      ) => {
        const canvas = await html2canvas(element, {
          scale: 2,
          logging: false,
          useCORS: true,
          allowTaint: true,
          backgroundColor: "#ffffff"
        });
        const imgData = canvas.toDataURL("image/png");
        const imgHeight = (canvas.height * contentWidth) / canvas.width;

        if (startNewPage) {
          pdf.addPage();
        }

        const availableHeight = pageHeight - margin - yOffset;

        pdf.addImage(imgData, "PNG", margin, yOffset, contentWidth, imgHeight);
        let remainingHeight = imgHeight - availableHeight;

        // If content overflows, add more pages
        while (remainingHeight > 0) {
          pdf.addPage();
          const srcY = imgHeight - remainingHeight;
          pdf.addImage(
            imgData,
            "PNG",
            margin,
            margin - srcY,
            contentWidth,
            imgHeight
          );
          remainingHeight -= pageHeight - margin * 2;
        }
      };

      // Build a temporary off-screen container for rendering
      const container = document.createElement("div");
      container.style.position = "absolute";
      container.style.left = "-9999px";
      container.style.top = "0";
      container.style.width = "800px";
      container.style.background = "#ffffff";
      container.style.padding = "24px";
      container.style.fontFamily = "Arial, sans-serif";
      document.body.appendChild(container);

      // Format month helper
      const formatMonth = (m?: string) => {
        if (!m) return "";
        try {
          const year = m.slice(0, 4);
          const mon = m.slice(-2);
          const date = new Date(Number(year), Number(mon) - 1);
          return date.toLocaleDateString("en-GB", {
            month: "long",
            year: "numeric"
          });
        } catch {
          return m;
        }
      };

      // Build header HTML to include in the first rendered section
      const headerHtml = `
        <div style="margin-bottom:20px">
          <img src="${mnjLogo}" alt="M&J Logo" style="height:100px;margin-bottom:8px" />
          <div style="font-size:20px;font-weight:bold;color:#1e1e1e">${projectCentralRepoData.projectName || "Forecast Summary"}</div>
          ${selectedMonth ? `<div style="font-size:13px;color:#646464;margin-top:4px">Forecast Month: ${formatMonth(selectedMonth)}</div>` : ""}
        </div>
      `;

      let isFirstSection = true;

      // Render cost sections (all on the same starting page)
      if (hasAnyCostSection) {
        const costContainer = document.createElement("div");
        costContainer.innerHTML = headerHtml;

        if (
          selectedPrintSections.has("labourCosts") &&
          projectCosts.labourCosts.length > 0
        ) {
          costContainer.innerHTML += `
            <div style="margin-bottom:20px">
              <h3 style="color:#1890ff;margin-bottom:8px">Labour Costs</h3>
              <table style="width:100%;border-collapse:collapse;font-size:13px">
                <thead>
                  <tr style="background:#fafafa;border-bottom:2px solid #e8e8e8">
                    <th style="padding:8px;text-align:left">Person</th>
                    <th style="padding:8px;text-align:right">Rate/Hour</th>
                    <th style="padding:8px;text-align:right">Hours/Day</th>
                    <th style="padding:8px;text-align:right">Day Rate</th>
                    <th style="padding:8px;text-align:right">Days</th>
                    <th style="padding:8px;text-align:right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  ${projectCosts.labourCosts
                    .map(
                      (c) => `
                    <tr style="border-bottom:1px solid #f0f0f0">
                      <td style="padding:8px">${c.name || "-"}</td>
                      <td style="padding:8px;text-align:right">£${c.ratePerHour.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td style="padding:8px;text-align:right">${c.hoursPerDay}</td>
                      <td style="padding:8px;text-align:right">£${getLabourDayRate(c).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td style="padding:8px;text-align:right">${c.noOfDays}</td>
                      <td style="padding:8px;text-align:right;color:#1890ff;font-weight:600">£${c.amount.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    </tr>
                  `
                    )
                    .join("")}
                </tbody>
                <tfoot>
                  <tr style="background:#e6f7ff;font-weight:bold">
                    <td colspan="5" style="padding:8px">Labour Total</td>
                    <td style="padding:8px;text-align:right;color:#1890ff">£${totalLabour.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          `;
        }

        if (
          selectedPrintSections.has("plantEquipment") &&
          projectCosts.plantCosts.length > 0
        ) {
          costContainer.innerHTML += `
            <div style="margin-bottom:20px">
              <h3 style="color:#FA8C16;margin-bottom:8px">Plant & Equipment</h3>
              <table style="width:100%;border-collapse:collapse;font-size:13px">
                <thead>
                  <tr style="background:#fafafa;border-bottom:2px solid #e8e8e8">
                    <th style="padding:8px;text-align:left">Category</th>
                    <th style="padding:8px;text-align:left">Unit</th>
                    <th style="padding:8px;text-align:right">Unit Cost</th>
                    <th style="padding:8px;text-align:right">Days</th>
                    <th style="padding:8px;text-align:right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  ${projectCosts.plantCosts
                    .map(
                      (c) => `
                    <tr style="border-bottom:1px solid #f0f0f0">
                      <td style="padding:8px">${c.name || "-"}</td>
                      <td style="padding:8px">${c.unitOfMeasure || "-"}</td>
                      <td style="padding:8px;text-align:right">£${c.unitCost.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td style="padding:8px;text-align:right">${c.noOfDays}</td>
                      <td style="padding:8px;text-align:right;color:#FA8C16;font-weight:600">£${c.amount.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    </tr>
                  `
                    )
                    .join("")}
                </tbody>
                <tfoot>
                  <tr style="background:#fff7e6;font-weight:bold">
                    <td colspan="4" style="padding:8px">Plant Total</td>
                    <td style="padding:8px;text-align:right;color:#FA8C16">£${totalPlant.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          `;
        }

        if (
          selectedPrintSections.has("materials") &&
          projectCosts.materialCosts.length > 0
        ) {
          costContainer.innerHTML += `
            <div style="margin-bottom:20px">
              <h3 style="color:#22C55E;margin-bottom:8px">Materials</h3>
              <table style="width:100%;border-collapse:collapse;font-size:13px">
                <thead>
                  <tr style="background:#fafafa;border-bottom:2px solid #e8e8e8">
                    <th style="padding:8px;text-align:left">Item</th>
                    <th style="padding:8px;text-align:left">Type</th>
                    <th style="padding:8px;text-align:right">Estimate</th>
                    <th style="padding:8px;text-align:right">% of Estimate</th>
                    <th style="padding:8px;text-align:right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  ${projectCosts.materialCosts
                    .map(
                      (c) => `
                    <tr style="border-bottom:1px solid #f0f0f0">
                      <td style="padding:8px">Bulk Material</td>
                      <td style="padding:8px">Bulk Material</td>
                      <td style="padding:8px;text-align:right">£${projectCosts.estimatedMaterialCost.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td style="padding:8px;text-align:right">${getMaterialPercent(c.amount, projectCosts.estimatedMaterialCost)}%</td>
                      <td style="padding:8px;text-align:right;color:#22C55E;font-weight:600">£${c.amount.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    </tr>
                  `
                    )
                    .join("")}
                </tbody>
                <tfoot>
                  <tr style="background:#f0fdf4;font-weight:bold">
                    <td colspan="4" style="padding:8px">Materials Total</td>
                    <td style="padding:8px;text-align:right;color:#22C55E">£${totalMaterial.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          `;
        }

        if (
          selectedPrintSections.has("subcontractors") &&
          projectCosts.subcontractorCosts.length > 0
        ) {
          costContainer.innerHTML += `
            <div style="margin-bottom:20px">
              <h3 style="color:#7C3AED;margin-bottom:8px">Subcontractors</h3>
              <table style="width:100%;border-collapse:collapse;font-size:13px">
                <thead>
                  <tr style="background:#fafafa;border-bottom:2px solid #e8e8e8">
                    <th style="padding:8px;text-align:left">Name</th>
                    <th style="padding:8px;text-align:right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  ${projectCosts.subcontractorCosts
                    .map(
                      (c) => `
                    <tr style="border-bottom:1px solid #f0f0f0">
                      <td style="padding:8px">${c.name ? getSubcontractorName(c.name) : "-"}</td>
                      <td style="padding:8px;text-align:right;color:#7C3AED;font-weight:600">£${c.amount.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    </tr>
                  `
                    )
                    .join("")}
                </tbody>
                <tfoot>
                  <tr style="background:#f5f3ff;font-weight:bold">
                    <td style="padding:8px">Subcontractors Total</td>
                    <td style="padding:8px;text-align:right;color:#7C3AED">£${totalSubcontractor.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          `;
        }

        if (costContainer.innerHTML.trim()) {
          container.innerHTML = "";
          container.appendChild(costContainer);
          await addElementToPdf(container, false);
          isFirstSection = false;
        }
      }

      // Plot Detail — always starts on a new page
      if (hasPlotDetail) {
        const plotContainer = document.createElement("div");
        plotContainer.style.padding = "0";

        let plotHtml = isFirstSection ? headerHtml : "";
        plotHtml += `<h3 style="color:#0891b2;margin-bottom:16px">Plot Details</h3>`;
        plotHtml += `<div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px">`;

        finalAssignments.forEach((assignment) => {
          plotHtml += `
            <div style="border:1px solid #e5e7eb;border-radius:8px;padding:12px">
              <div style="font-weight:600;margin-bottom:4px">Plot ${assignment.plotNumber}</div>
              <div style="font-size:12px;color:#6b7280;margin-bottom:8px">${assignment.stages.length} stages</div>
              <div style="border-top:1px solid #f3f4f6;padding-top:8px">
                <div style="font-size:11px;color:#9ca3af;text-transform:uppercase;margin-bottom:4px">Stages Selected</div>
                <div style="display:flex;flex-wrap:wrap;gap:4px">
                  ${assignment.stages
                    .map(
                      (s) =>
                        `<span style="background:${stageColors[s.name] || "#1890ff"};color:#fff;padding:2px 8px;border-radius:4px;font-size:11px">${s.name}</span>`
                    )
                    .join("")}
                </div>
              </div>
            </div>
          `;
        });
        plotHtml += `</div>`;

        plotContainer.innerHTML = plotHtml;
        container.innerHTML = "";
        container.appendChild(plotContainer);
        const plotStartsNewPage = !isFirstSection;
        await addElementToPdf(container, plotStartsNewPage);
        isFirstSection = false;
      }

      // Forecast vs Budget Summary — always starts on a new page
      if (hasForecastVsBudget) {
        const forecastContainer = document.createElement("div");
        forecastContainer.style.padding = "0";

        // Recalculate values for the summary table
        const variationAmt =
          variations?.length > 0
            ? variations.reduce(
                (acc, cur) =>
                  acc + (cur.utilizedAmount > 0 ? cur.utilizedAmount : 0),
                0
              )
            : 0;

        const forecastTotal =
          totalLabour +
          totalPlant +
          totalMaterial +
          totalSubcontractor +
          variationAmt;

        const allocatedTaskIds = new Set(taskAllocations.map((t) => t.taskId));

        let budgetLabour = 0;
        let budgetPlant = 0;
        let budgetMaterial = 0;
        let budgetSubcontractor = 0;

        projectCentralRepoData.groundworks?.plots?.forEach((plot) => {
          plot.stages?.forEach((stage) => {
            stage.tasks?.forEach((task) => {
              if (allocatedTaskIds.has(task.taskId) && task.costBreakdown) {
                budgetLabour += task.costBreakdown.labour_rate || 0;
                budgetPlant += task.costBreakdown.plant_rate || 0;
                budgetMaterial += task.costBreakdown.material_rate || 0;
                budgetSubcontractor +=
                  task.costBreakdown.subcontractor_rate || 0;
              }
            });
          });
        });

        projectCentralRepoData.roadsAndSewers?.runs?.forEach((run) => {
          run.stages?.forEach((stage) => {
            stage.tasks?.forEach((task) => {
              if (allocatedTaskIds.has(task.taskId) && task.costBreakdown) {
                budgetLabour += task.costBreakdown.labour_rate || 0;
                budgetPlant += task.costBreakdown.plant_rate || 0;
                budgetMaterial += task.costBreakdown.material_rate || 0;
                budgetSubcontractor +=
                  task.costBreakdown.subcontractor_rate || 0;
              }
            });
          });
        });

        const budgetTotal =
          budgetLabour +
          budgetPlant +
          budgetMaterial +
          budgetSubcontractor +
          variationAmt;
        const totalVariance = budgetTotal - forecastTotal;

        const rows = [
          { label: "Labour", forecast: totalLabour, budget: budgetLabour },
          { label: "Plant", forecast: totalPlant, budget: budgetPlant },
          {
            label: "Material",
            forecast: totalMaterial,
            budget: budgetMaterial
          },
          {
            label: "Subcontractor",
            forecast: totalSubcontractor,
            budget: budgetSubcontractor
          }
        ];

        if (features.showVariationRow) {
          rows.push({
            label: "Variation",
            forecast: variationAmt,
            budget: variationAmt
          });
        }

        const fmt = (v: number) =>
          `£${v.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        const varColor = (v: number) =>
          v > 0 ? "#52c41a" : v < 0 ? "#ff4d4f" : "#595959";

        let tblHtml = isFirstSection ? headerHtml : "";
        tblHtml += `
          <h3 style="margin-bottom:8px">Forecast vs Tender Budget Summary</h3>
          <p style="color:#8c8c8c;font-size:12px;margin-bottom:12px">Comparison between forecasted costs and original budget/cost from tender data</p>
          <table style="width:100%;border-collapse:collapse;font-size:13px">
            <thead>
              <tr style="background:#fafafa;border-bottom:2px solid #e8e8e8">
                <th style="padding:8px;text-align:left">Category</th>
                <th style="padding:8px;text-align:right">Forecast</th>
                <th style="padding:8px;text-align:right">Tender Budget</th>
                <th style="padding:8px;text-align:right">Variance</th>
                <th style="padding:8px;text-align:right">Variance %</th>
              </tr>
            </thead>
            <tbody>
        `;

        rows.forEach((r) => {
          const variance = r.budget - r.forecast;
          const pct =
            r.budget !== 0
              ? Math.abs((variance / r.budget) * 100).toFixed(2)
              : "0.00";
          tblHtml += `
            <tr style="border-bottom:1px solid #f0f0f0">
              <td style="padding:8px;font-weight:600">${r.label}</td>
              <td style="padding:8px;text-align:right;color:#1890ff;font-weight:600">${fmt(r.forecast)}</td>
              <td style="padding:8px;text-align:right;color:#595959;font-weight:600">${fmt(r.budget)}</td>
              <td style="padding:8px;text-align:right;color:${varColor(variance)};font-weight:600">${fmt(Math.abs(variance))}</td>
              <td style="padding:8px;text-align:right;color:${varColor(variance)};font-weight:600">${pct}%</td>
            </tr>
          `;
        });

        const totalPct =
          budgetTotal !== 0
            ? Math.abs((totalVariance / budgetTotal) * 100).toFixed(2)
            : "0.00";

        tblHtml += `
            </tbody>
            <tfoot>
              <tr style="background:#fafafa;font-weight:bold;font-size:14px">
                <td style="padding:8px">Total</td>
                <td style="padding:8px;text-align:right;color:#1890ff">${fmt(forecastTotal)}</td>
                <td style="padding:8px;text-align:right;color:#595959">${fmt(budgetTotal)}</td>
                <td style="padding:8px;text-align:right;color:${varColor(totalVariance)}">${fmt(Math.abs(totalVariance))}</td>
                <td style="padding:8px;text-align:right;color:${varColor(totalVariance)}">${totalPct}%</td>
              </tr>
            </tfoot>
          </table>
        `;

        forecastContainer.innerHTML = tblHtml;
        container.innerHTML = "";
        container.appendChild(forecastContainer);
        const forecastStartsNewPage = !isFirstSection;
        await addElementToPdf(container, forecastStartsNewPage);
      }

      // Clean up temporary container
      document.body.removeChild(container);

      pdf.save("forecast-summary.pdf");
      setShowPrintDialog(false);
    } catch (error) {
      console.error("Error generating PDF:", error);
    } finally {
      setIsPrinting(false);
    }
  }, [
    selectedPrintSections,
    projectCosts,
    totalLabour,
    totalPlant,
    totalMaterial,
    totalSubcontractor,
    variationCost,
    finalAssignments,
    stageColors,
    variations,
    taskAllocations,
    projectCentralRepoData,
    selectedPlots,
    selectedMonth,
    getSubcontractorName
  ]);

  return (
    <div ref={printRef}>
      {/* Print Button Row */}
      <div className="flex justify-end mb-4">
        <Button
          type="default"
          icon={<PrinterOutlined />}
          onClick={() => setShowPrintDialog(true)}
          className="flex items-center gap-2"
        >
          Print
        </Button>
      </div>

      {/* Print Dialog Modal */}
      <Modal
        title="Print"
        open={showPrintDialog}
        onCancel={() => setShowPrintDialog(false)}
        footer={[
          <Button
            key="cancel"
            size="large"
            onClick={() => setShowPrintDialog(false)}
          >
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            size="large"
            loading={isPrinting}
            disabled={selectedPrintSections.size === 0}
            onClick={handlePrint}
          >
            Save
          </Button>
        ]}
        width={448}
        centered
      >
        <Text type="secondary">
          Please Select the fields that you would like to print
        </Text>

        <Divider style={{ margin: "12px 0" }} />

        {/* Section Checkboxes */}
        <div className="flex flex-col gap-1">
          {PRINT_SECTIONS.map((section) => (
            <div
              key={section.key}
              className="py-2 border-b border-gray-100 last:border-b-0"
            >
              <Checkbox
                checked={selectedPrintSections.has(section.key)}
                onChange={() => togglePrintSection(section.key)}
              >
                {section.label}
              </Checkbox>
            </div>
          ))}
        </div>

        {/* Selected Tags */}
        {selectedPrintSections.size > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {PRINT_SECTIONS.filter((s) => selectedPrintSections.has(s.key)).map(
              (section) => (
                <Tag
                  key={section.key}
                  closable
                  onClose={() => togglePrintSection(section.key)}
                >
                  {section.label}
                </Tag>
              )
            )}
          </div>
        )}
      </Modal>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
        {/* Total Budget Card */}
        <div className="bg-[#F1F3F9] border border-solid border-gray-200 rounded-lg p-4 shadow-sm">
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
              <TbWallet className="text-blue-500 text-xl" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">
                £
                {totalBudget.toLocaleString("en-GB", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
              </div>
            </div>
          </div>
          <div className="mt-1 text-sm text-gray-500">Total Budget</div>
        </div>

        {/* Labour Card */}
        <div className="bg-[#F1F3F9] border border-solid border-gray-200 rounded-lg p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
              <TeamOutlined className="text-blue-500 text-lg" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">
                £
                {totalLabour.toLocaleString("en-GB", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
              </div>
            </div>
          </div>
          <div className="mt-1 text-sm text-gray-500">Labour</div>
        </div>

        {/* Plant Card */}
        <div className="bg-[#F1F3F9] border border-solid border-gray-200 rounded-lg p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#FEF3C7] flex items-center justify-center">
              <LuTruck className="text-[#F59E0B] text-lg" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">
                £
                {totalPlant.toLocaleString("en-GB", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
              </div>
            </div>
          </div>
          <div className="mt-1 text-sm text-gray-500">Plant</div>
        </div>

        {/* Materials Card */}
        <div className="bg-[#F1F3F9] border border-solid border-gray-200 rounded-lg p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#F0FDF4] flex items-center justify-center">
              <BsBoxSeam className="text-[#22C55E] text-lg" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">
                £
                {totalMaterial.toLocaleString("en-GB", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
              </div>
            </div>
          </div>
          <div className="mt-1 text-sm text-gray-500">Materials</div>
        </div>

        {/* Subcontractors Card */}
        <div className="bg-[#F1F3F9] border border-solid border-gray-200 rounded-lg p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
              <HiOutlineUsers className="text-purple-600 text-lg" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">
                £
                {totalSubcontractor.toLocaleString("en-GB", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
              </div>
            </div>
          </div>
          <div className="mt-1 text-sm text-gray-500">Subcontractors</div>
        </div>

        {/* Total Plots Card */}
        <div className="bg-[#F1F3F9] border border-solid border-gray-200 rounded-lg p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-cyan-100 flex items-center justify-center">
              <MdOutlineLocationOn className="text-cyan-600 text-lg" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">
                {finalAssignments.length}
              </div>
            </div>
          </div>
          <div className="mt-1 text-sm text-gray-500">Total Plots</div>
        </div>
      </div>

      {/* Project Cost Details - Collapsible */}
      <div className="bg-white border border-solid border-[#E5E7EB] rounded-xl mb-6 overflow-hidden">
        <div
          className="bg-gray-50 px-4 py-3 flex items-center justify-between cursor-pointer"
          onClick={() => toggleSummarySection("projectCostDetails")}
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
              <TbWallet className="text-blue-500 text-lg" />
            </div>
            <div>
              <div className="font-semibold text-gray-900">
                Project Cost Details
              </div>
              <div className="text-xs text-gray-500">
                Labour, Plant, Materials & Subcontractors breakdown
              </div>
            </div>
          </div>
          <Button
            type="text"
            icon={
              <DownOutlined
                className={`text-gray-400 transition-transform duration-200 ${summaryExpandedSections.projectCostDetails ? "rotate-180" : ""}`}
              />
            }
            size="small"
          />
        </div>

        {summaryExpandedSections.projectCostDetails && (
          <div className="p-4">
            {/* Labour Costs Table */}
            {projectCosts.labourCosts.length > 0 && (
              <>
                <div className="flex items-center gap-2 mb-3">
                  <TeamOutlined className="text-blue-500" />
                  <Title level={5} className="!mb-0">
                    Labour Costs
                  </Title>
                </div>
                <Table
                  dataSource={projectCosts.labourCosts}
                  rowKey="id"
                  size="small"
                  pagination={false}
                  style={{ marginBottom: 24 }}
                  columns={[
                    {
                      title: "Name",
                      dataIndex: "name",
                      key: "name",
                      render: (name: string) => <Text>{name || "-"}</Text>
                    },
                    {
                      title: "Rate/Hour",
                      dataIndex: "ratePerHour",
                      key: "ratePerHour",
                      align: "right" as const,
                      render: (rate: number) => (
                        <Text>
                          £
                          {rate.toLocaleString("en-GB", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                        </Text>
                      )
                    },
                    {
                      title: "Hours/Day",
                      dataIndex: "hoursPerDay",
                      key: "hoursPerDay",
                      align: "right" as const
                    },
                    {
                      title: "Day Rate",
                      key: "dayRate",
                      align: "right" as const,
                      render: (_, entry) => (
                        <Text>
                          £
                          {getLabourDayRate(entry).toLocaleString("en-GB", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                        </Text>
                      )
                    },
                    {
                      title: "Days",
                      dataIndex: "noOfDays",
                      key: "noOfDays",
                      align: "right" as const
                    },
                    {
                      title: "Amount",
                      key: "amount",
                      align: "right" as const,
                      render: (_, entry) => (
                        <Text strong style={{ color: "#1890FF" }}>
                          £
                          {entry.amount.toLocaleString("en-GB", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                        </Text>
                      )
                    }
                  ]}
                  summary={(data) => (
                    <Table.Summary>
                      <Table.Summary.Row className="bg-blue-50">
                        <Table.Summary.Cell index={0} colSpan={5}>
                          <Text strong>Labour Total</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1} align="right">
                          <Text strong style={{ color: "#1890FF" }}>
                            £
                            {data
                              .reduce((sum, entry) => sum + entry.amount, 0)
                              .toLocaleString("en-GB", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}
                          </Text>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    </Table.Summary>
                  )}
                />
              </>
            )}

            {/* Plant Costs Table */}
            {projectCosts.plantCosts.length > 0 && (
              <>
                <div className="flex items-center gap-2 mb-3">
                  <LuTruck className="text-[#F59E0B]" />
                  <Title level={5} className="!mb-0">
                    Plant & Equipment
                  </Title>
                </div>
                <Table
                  dataSource={projectCosts.plantCosts}
                  rowKey="id"
                  size="small"
                  pagination={false}
                  style={{ marginBottom: 24 }}
                  columns={[
                    {
                      title: "Category",
                      dataIndex: "name",
                      key: "name",
                      render: (name: string) => <Text>{name || "-"}</Text>
                    },
                    {
                      title: "Unit",
                      dataIndex: "unitOfMeasure",
                      key: "unitOfMeasure",
                      render: (unit: string) => <Text>{unit || "-"}</Text>
                    },
                    {
                      title: "Unit Cost",
                      dataIndex: "unitCost",
                      key: "unitCost",
                      align: "right" as const,
                      render: (cost: number) => (
                        <Text>
                          £
                          {cost.toLocaleString("en-GB", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                        </Text>
                      )
                    },
                    {
                      title: "Days",
                      dataIndex: "noOfDays",
                      key: "noOfDays",
                      align: "right" as const
                    },
                    {
                      title: "Amount",
                      key: "amount",
                      align: "right" as const,
                      render: (_, entry) => (
                        <Text strong style={{ color: "#FA8C16" }}>
                          £
                          {entry.amount.toLocaleString("en-GB", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                        </Text>
                      )
                    }
                  ]}
                  summary={(data) => (
                    <Table.Summary>
                      <Table.Summary.Row className="bg-orange-50">
                        <Table.Summary.Cell index={0} colSpan={4}>
                          <Text strong>Plant Total</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1} align="right">
                          <Text strong style={{ color: "#FA8C16" }}>
                            £
                            {data
                              .reduce((sum, entry) => sum + entry.amount, 0)
                              .toLocaleString("en-GB", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}
                          </Text>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    </Table.Summary>
                  )}
                />
              </>
            )}

            {/* Material Costs Table */}
            {projectCosts.materialCosts.length > 0 && (
              <>
                <div className="flex items-center gap-2 mb-3">
                  <BsBoxSeam className="text-[#22C55E]" />
                  <Title level={5} className="!mb-0">
                    Materials
                  </Title>
                </div>
                <Table
                  dataSource={projectCosts.materialCosts}
                  rowKey="id"
                  size="small"
                  pagination={false}
                  style={{ marginBottom: 24 }}
                  columns={[
                    {
                      title: "Item",
                      dataIndex: "materialType",
                      key: "materialType",
                      render: () => <Text>Bulk Material</Text>
                    },
                    {
                      title: "Type",
                      key: "type",
                      render: () => <Text>Bulk Material</Text>
                    },
                    {
                      title: "Estimate",
                      key: "estimate",
                      align: "right" as const,
                      render: () => (
                        <Text>
                          £
                          {projectCosts.estimatedMaterialCost.toLocaleString(
                            "en-GB",
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            }
                          )}
                        </Text>
                      )
                    },
                    {
                      title: "% of Estimate",
                      key: "percentOfEstimate",
                      align: "right" as const,
                      render: (_, entry) => (
                        <Text>
                          {getMaterialPercent(
                            entry.amount,
                            projectCosts.estimatedMaterialCost
                          )}
                          %
                        </Text>
                      )
                    },
                    {
                      title: "Amount",
                      key: "amount",
                      align: "right" as const,
                      render: (_, entry) => (
                        <Text strong style={{ color: "#22C55E" }}>
                          £
                          {entry.amount.toLocaleString("en-GB", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                        </Text>
                      )
                    }
                  ]}
                  summary={(data) => (
                    <Table.Summary>
                      <Table.Summary.Row className="bg-green-50">
                        <Table.Summary.Cell index={0} colSpan={4}>
                          <Text strong>Materials Total</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1} align="right">
                          <Text strong style={{ color: "#22C55E" }}>
                            £
                            {data
                              .reduce((sum, entry) => sum + entry.amount, 0)
                              .toLocaleString("en-GB", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}
                          </Text>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    </Table.Summary>
                  )}
                />
              </>
            )}

            {/* Subcontractor Costs Table */}
            {projectCosts.subcontractorCosts.length > 0 && (
              <>
                <div className="flex items-center gap-2 mb-3">
                  <HiOutlineUsers className="text-purple-600" />
                  <Title level={5} className="!mb-0">
                    Subcontractors
                  </Title>
                </div>
                <Table
                  dataSource={projectCosts.subcontractorCosts}
                  rowKey="name"
                  size="small"
                  pagination={false}
                  style={{ marginBottom: 16 }}
                  columns={[
                    {
                      title: "Name",
                      dataIndex: "name",
                      key: "name",
                      render: (no: string) => (
                        <Text>{no ? getSubcontractorName(no) : "-"}</Text>
                      )
                    },
                    {
                      title: "Amount",
                      key: "amount",
                      align: "right" as const,
                      render: (_, entry) => (
                        <Text strong style={{ color: "#7C3AED" }}>
                          £
                          {entry.amount.toLocaleString("en-GB", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                        </Text>
                      )
                    }
                  ]}
                  summary={(data) => (
                    <Table.Summary>
                      <Table.Summary.Row className="bg-purple-50">
                        <Table.Summary.Cell index={0}>
                          <Text strong>Subcontractors Total</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1} align="right">
                          <Text strong style={{ color: "#7C3AED" }}>
                            £
                            {data
                              .reduce((sum, entry) => sum + entry.amount, 0)
                              .toLocaleString("en-GB", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}
                          </Text>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    </Table.Summary>
                  )}
                />
              </>
            )}

            {/* Grand Total */}
            <div className="font-mono bg-[#f1f3f9] text-[#1e1e1e] rounded-[10px] px-5 py-2 mt-4 flex justify-between items-center">
              <span className="font-medium text-lg">Grand Total</span>
              <span className="font-medium text-lg">
                £
                {totalBudget.toLocaleString("en-GB", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Plot Details - Collapsible */}
      <div className="bg-white border border-solid border-[#E5E7EB] rounded-xl mb-6 overflow-hidden">
        <div
          className="bg-gray-50 px-4 py-3 flex items-center justify-between cursor-pointer"
          onClick={() => toggleSummarySection("plotDetails")}
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-cyan-100 flex items-center justify-center">
              <MdOutlineLocationOn className="text-cyan-600 text-lg" />
            </div>
            <div>
              <div className="font-semibold text-gray-900">Plot Details</div>
              <div className="text-xs text-gray-500">
                Selected plots and their assigned stages
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">
              {finalAssignments.filter((a) => a.stages.length > 0).length} plots
              selected
            </span>
            <Button
              type="text"
              icon={
                <DownOutlined
                  className={`text-gray-400 transition-transform duration-200 ${summaryExpandedSections.plotDetails ? "rotate-180" : ""}`}
                />
              }
              size="small"
            />
          </div>
        </div>

        {summaryExpandedSections.plotDetails && (
          <div className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {finalAssignments
                .filter((a) => a.stages.length > 0)
                .map((assignment) => (
                  <div
                    key={assignment.plotNumber}
                    className="bg-white border border-solid border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
                          <MdOutlineLocationOn className="text-blue-600 text-sm" />
                        </div>
                        <span className="font-semibold text-gray-900">
                          Plot {assignment.plotNumber}
                        </span>
                      </div>
                      <DownOutlined className="text-gray-400 text-xs" />
                    </div>
                    <div className="text-xs text-gray-500 mb-2">
                      {assignment.stages.length} stages
                    </div>
                    <div className="border-t border-gray-100 pt-3">
                      <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">
                        Stages Selected
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {assignment.stages.map((stage) => (
                          <Tag
                            key={stage.id}
                            color={stageColors[stage.name] || "#1890ff"}
                            className="text-xs"
                          >
                            {stage.name}
                          </Tag>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>

      {/* Forecast vs Budget Summary */}
      <div className="bg-white border border-solid border-[#E5E7EB] rounded-xl overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
          <Title level={5} className="!mb-1">
            Forecast vs Tender Budget Summary
          </Title>
          <Text type="secondary" className="text-sm">
            Comparison between forecasted costs and original budget/cost from
            tender data
          </Text>
        </div>

        {(() => {
          const variationAmt =
            variations?.length > 0
              ? variations.reduce(
                  (acc, cur) =>
                    acc + (cur.utilizedAmount > 0 ? cur.utilizedAmount : 0),
                  0
                )
              : 0;

          // Calculate Forecast totals (from project costs)
          const forecastLabour = totalLabour;
          const forecastPlant = totalPlant;
          const forecastMaterial = totalMaterial;
          const forecastSubcontractor = totalSubcontractor;
          const forecastTotal =
            forecastLabour +
            forecastPlant +
            forecastMaterial +
            forecastSubcontractor +
            variationAmt;

          // Calculate Budget totals by summing each selected task's own costBreakdown
          const allocatedTaskIds = new Set(
            taskAllocations.map((t) => t.taskId)
          );

          let budgetLabour = 0;
          let budgetPlant = 0;
          let budgetMaterial = 0;
          let budgetSubcontractor = 0;

          // Sum task-level costBreakdown from groundworks
          projectCentralRepoData.groundworks?.plots?.forEach((plot) => {
            plot.stages?.forEach((stage) => {
              stage.tasks?.forEach((task) => {
                if (allocatedTaskIds.has(task.taskId) && task.costBreakdown) {
                  budgetLabour += task.costBreakdown.labour_rate || 0;
                  budgetPlant += task.costBreakdown.plant_rate || 0;
                  budgetMaterial += task.costBreakdown.material_rate || 0;
                  budgetSubcontractor +=
                    task.costBreakdown.subcontractor_rate || 0;
                }
              });
            });
          });

          // Sum task-level costBreakdown from roadsAndSewers
          projectCentralRepoData.roadsAndSewers?.runs?.forEach((run) => {
            run.stages?.forEach((stage) => {
              stage.tasks?.forEach((task) => {
                if (allocatedTaskIds.has(task.taskId) && task.costBreakdown) {
                  budgetLabour += task.costBreakdown.labour_rate || 0;
                  budgetPlant += task.costBreakdown.plant_rate || 0;
                  budgetMaterial += task.costBreakdown.material_rate || 0;
                  budgetSubcontractor +=
                    task.costBreakdown.subcontractor_rate || 0;
                }
              });
            });
          });

          const budgetTotal =
            budgetLabour +
            budgetPlant +
            budgetMaterial +
            budgetSubcontractor +
            variationAmt;

          // Calculate variances (budget - forecast)
          const labourVariance = budgetLabour - forecastLabour;
          const plantVariance = budgetPlant - forecastPlant;
          const materialVariance = budgetMaterial - forecastMaterial;
          const subcontractorVariance =
            budgetSubcontractor - forecastSubcontractor;
          const totalVariance = budgetTotal - forecastTotal;

          // Helper function to get variance color
          // Variance = budget - forecast
          // Positive variance means budget > forecast (under budget = good = green)
          // Negative variance means budget < forecast (over budget = bad = red)
          const getVarianceColor = (variance: number) => {
            const roundedVariance = Number(variance.toFixed(2));
            if (roundedVariance > 0) return "#52c41a";
            if (roundedVariance < 0) return "#ff4d4f";
            return "#595959";
          };

          // Helper function to format variance with sign
          const formatVariance = (variance: number) => {
            return `£${Math.abs(variance).toLocaleString("en-GB", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}`;
          };

          const comparisonData = [
            {
              key: "labour",
              category: "Labour",
              forecast: forecastLabour,
              budget: budgetLabour,
              variance: labourVariance
            },
            {
              key: "plant",
              category: "Plant",
              forecast: forecastPlant,
              budget: budgetPlant,
              variance: plantVariance
            },
            {
              key: "material",
              category: "Material",
              forecast: forecastMaterial,
              budget: budgetMaterial,
              variance: materialVariance
            },
            {
              key: "subcontractor",
              category: "Subcontractor",
              forecast: forecastSubcontractor,
              budget: budgetSubcontractor,
              variance: subcontractorVariance
            }
          ];

          if (features.showVariationRow) {
            comparisonData.push({
              key: "variation",
              category: "Variation",
              forecast: variationAmt,
              budget: variationAmt,
              variance: 0
            });
          }

          return (
            <div className="p-4">
              <Table
                dataSource={comparisonData}
                rowKey="key"
                size="small"
                pagination={false}
                columns={[
                  {
                    title: "Category",
                    dataIndex: "category",
                    key: "category",
                    width: 150,
                    render: (text: string) => <Text strong>{text}</Text>
                  },
                  {
                    title: "Forecast",
                    dataIndex: "forecast",
                    key: "forecast",
                    align: "right" as const,
                    width: 150,
                    render: (value: number) => (
                      <Text style={{ color: "#1890ff", fontWeight: "600" }}>
                        £
                        {value.toLocaleString("en-GB", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                      </Text>
                    )
                  },
                  {
                    title: "Tender Budget",
                    dataIndex: "budget",
                    key: "budget",
                    align: "right" as const,
                    width: 150,
                    render: (value: number) => (
                      <Text style={{ color: "#595959", fontWeight: "600" }}>
                        £
                        {value.toLocaleString("en-GB", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                      </Text>
                    )
                  },
                  {
                    title: "Variance",
                    dataIndex: "variance",
                    key: "variance",
                    align: "right" as const,
                    width: 150,
                    render: (value: number) => (
                      <Text strong style={{ color: getVarianceColor(value) }}>
                        {formatVariance(value)}
                      </Text>
                    )
                  },
                  {
                    title: "Variance %",
                    key: "variancePercent",
                    align: "right" as const,
                    width: 120,
                    render: (
                      _: unknown,
                      record: { budget: number; variance: number }
                    ) => {
                      const percent =
                        record.budget !== 0
                          ? (record.variance / record.budget) * 100
                          : 0;
                      return (
                        <Text
                          strong
                          style={{ color: getVarianceColor(record.variance) }}
                        >
                          {Math.abs(percent).toFixed(2)}%
                        </Text>
                      );
                    }
                  }
                ]}
                summary={() => (
                  <Table.Summary>
                    <Table.Summary.Row style={{ background: "#fafafa" }}>
                      <Table.Summary.Cell index={0}>
                        <Text strong style={{ fontSize: "14px" }}>
                          Total
                        </Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="right">
                        <Text
                          strong
                          style={{ color: "#1890ff", fontSize: "14px" }}
                        >
                          £
                          {forecastTotal.toLocaleString("en-GB", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                        </Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2} align="right">
                        <Text
                          strong
                          style={{ color: "#595959", fontSize: "14px" }}
                        >
                          £
                          {budgetTotal.toLocaleString("en-GB", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                        </Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={3} align="right">
                        <Text
                          strong
                          style={{
                            color: getVarianceColor(totalVariance),
                            fontSize: "14px"
                          }}
                        >
                          {formatVariance(totalVariance)}
                        </Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={4} align="right">
                        <Text
                          strong
                          style={{
                            color: getVarianceColor(totalVariance),
                            fontSize: "14px"
                          }}
                        >
                          {budgetTotal !== 0
                            ? `${Math.abs((totalVariance / budgetTotal) * 100).toFixed(2)}%`
                            : "0.00%"}
                        </Text>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                )}
              />

              <div
                style={{
                  marginTop: 16,
                  padding: "12px",
                  background: "#f0f5ff",
                  borderRadius: "6px",
                  border: "1px solid #adc6ff"
                }}
              >
                <Text strong style={{ color: "#1890ff" }}>
                  Note:
                </Text>
                <Text style={{ marginLeft: 8, color: "#595959" }}>
                  Budget values are calculated from the cost breakdown of the
                  selected tasks across all stages and plots. These values
                  represent the original budgeted costs for Labour, Plant,
                  Materials, and Subcontractors assigned to the selected tasks.
                </Text>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};
